/**
 * Generates a placeholder profile picture with initials
 * Returns a data URL for an SVG image
 */
export const generatePlaceholderPicture = (firstName: string, lastName: string): string => {
  const initials = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();
  const colors = [
    '#2E7D32', '#1B5E20', '#4CAF50', '#1565C0', '#FFC107',
    '#D32F2F', '#7c3aed', '#FF6F00', '#00695C', '#5D4037'
  ];
  
  // Use a simple hash to consistently assign colors based on name
  const hash = (firstName + lastName).split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);
  const colorIndex = Math.abs(hash) % colors.length;
  const backgroundColor = colors[colorIndex];
  
  // Create SVG with initials
  const svg = `
    <svg width="200" height="200" xmlns="http://www.w3.org/2000/svg">
      <rect width="200" height="200" fill="${backgroundColor}"/>
      <text 
        x="50%" 
        y="50%" 
        font-family="Arial, sans-serif" 
        font-size="80" 
        font-weight="bold" 
        fill="white" 
        text-anchor="middle" 
        dominant-baseline="central"
      >
        ${initials}
      </text>
    </svg>
  `.trim();
  
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

/**
 * Checks if a profile picture is a real image (not a generated SVG placeholder)
 */
const isRealImage = (profilePicture: string): boolean => {
  // Real URLs are always real images
  if (profilePicture.startsWith('http://') || profilePicture.startsWith('https://')) {
    return true;
  }
  
  // Real image data URLs (jpeg, png, gif, webp, etc.) are real images
  if (profilePicture.startsWith('data:image/jpeg') || 
      profilePicture.startsWith('data:image/jpg') ||
      profilePicture.startsWith('data:image/png') ||
      profilePicture.startsWith('data:image/gif') ||
      profilePicture.startsWith('data:image/webp') ||
      profilePicture.startsWith('data:image/bmp')) {
    return true;
  }
  
  // SVG data URLs might be generated placeholders, so we need to check
  if (profilePicture.startsWith('data:image/svg+xml')) {
    // If it's a manually uploaded SVG (not our generated one), it's still a real image
    // We'll treat all SVGs as potentially real for now, unless we can detect it's generated
    // For simplicity, if someone manually sets an SVG, we'll use it
    return true;
  }
  
  return false;
};

/**
 * Gets the profile picture URL, using placeholder if not available
 * If user has a real profile picture (not a generated placeholder), use that instead of generating initials
 */
export const getProfilePicture = (user: { firstName: string; lastName: string; profilePicture?: string }): string => {
  // Check if user has a profile picture (not empty, not just whitespace)
  if (user.profilePicture && user.profilePicture.trim() !== '') {
    // If it's a real image (URL or real image data URL), use it
    if (isRealImage(user.profilePicture)) {
      return user.profilePicture;
    }
  }
  // Otherwise, generate placeholder with initials
  return generatePlaceholderPicture(user.firstName, user.lastName);
};

